import { Component, effect, inject, TemplateRef, viewChild, ViewEncapsulation } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { GS1FormModule, GS1LayoutModule, GS1ModalModule, GS1ModalService, GS1TooltipDirective, ModalState } from '@gs1france/gs1-components-library';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CompanyService } from '@services/company.service';
import { CustomerService } from '@services/customerService.service';
import { StorageService } from '@services/storage.service';
import { tap } from 'rxjs';

@Component({
  selector: 'unsubscribe',
  templateUrl: './unsubscribe.component.html',
  styleUrls: ['./unsubscribe.component.scss'],
  imports: [ReactiveFormsModule, RouterLink, TranslateModule, GS1LayoutModule, GS1FormModule, GS1TooltipDirective, GS1ModalModule],
  encapsulation: ViewEncapsulation.None
})
export class UnsubscribeComponent {
  private fb = inject(UntypedFormBuilder);

  private modalService = inject(GS1ModalService);
  private translateService = inject(TranslateService);

  private storageService = inject(StorageService);
  private companyService = inject(CompanyService);
  private customerService = inject(CustomerService);

  currentUser = toSignal(this.storageService.getUser());
  isUserAdmin = toSignal(this.storageService.isUserAdmin$());

  stepOneTemplate = viewChild<TemplateRef<HTMLTemplateElement>>('stepOneTemplate');
  stepTwoTemplate = viewChild<TemplateRef<HTMLTemplateElement>>('stepTwoTemplate');
  stepThreeTemplate = viewChild<TemplateRef<HTMLTemplateElement>>('stepThreeTemplate');

  unsubscribeForm = this.fb.group({
    reason: [null, Validators.required],
    phoneNumber: [null, Validators.required]
  });

  company = this.companyService.company;

  modalState = new ModalState();

  constructor() {
    effect(() => {
      const user = this.currentUser();
      if (user) this.unsubscribeForm.controls.phoneNumber.setValue(user.telPrincipal);
    });
  }

  openUnsubscribeWizardDialog(currentStep: number = 1) {
    const totalSteps: number = 3;

    const confirmText: string = currentStep !== totalSteps ? this.translateService.instant('footer.unsubscribe.next') : this.translateService.instant('footer.unsubscribe.submit');

    this.modalState.update({
      confirmText
    });

    const modalRef = this.modalService.open({
      title: this.translateService.instant('footer.unsubscribe.title'),
      template: this.getWizardStepContent(currentStep),
      confirm: confirmText,
      cancel: currentStep === 1 ? undefined : this.translateService.instant('footer.unsubscribe.back'),
      closeButton: false,
      closeOnNavigation: true,
      confirmDisabled: false,
      state: this.modalState,
      width: '670',
      maxWidth: '95vw'
    });

    modalRef
      .backdropClick()
      .pipe(
        tap(() => {
          currentStep = 1;
          this.modalState.update({ confirmDisabled: false });
          modalRef.close();
        })
      )
      .subscribe();

    modalRef
      .afterClosed()
      .pipe(
        tap((confirmed: boolean) => {
          if (confirmed) {
            if (currentStep < totalSteps) {
              currentStep++;
              this.openUnsubscribeWizardDialog(currentStep);
              if (currentStep > 1) this.modalState.update({ confirmDisabled: true });
            } else this.sendUnsubscribeRequest();
          } else if (currentStep > 1) {
            currentStep--;
            if (currentStep <= 1) this.modalState.update({ confirmDisabled: false });
            if (currentStep === 2) this.modalState.update({ confirmDisabled: true });
            this.openUnsubscribeWizardDialog(currentStep);
          }
        })
      )
      .subscribe();

    if (currentStep === 3)
      this.unsubscribeForm.valueChanges
        .pipe(
          tap(() => this.modalState.update({ confirmDisabled: this.unsubscribeForm.invalid }))
        )
        .subscribe();
  }

  getWizardStepContent(currentStep: number): TemplateRef<HTMLTemplateElement> {
    switch (currentStep) {
      case 2:
        return this.stepTwoTemplate();
      case 3:
        return this.stepThreeTemplate();
      default:
        return this.stepOneTemplate();
    }
  }

  updateAcceptUnsubscribe(accept: boolean) {
    this.modalState.update({ confirmDisabled: !accept });
  }

  sendUnsubscribeRequest() {
    if (this.unsubscribeForm.valid) this.customerService.unsubscribe(this.unsubscribeForm.value).subscribe();
  }
}
